/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import memoize from 'lodash.memoize';

import selectElementContents from '../utils/selectElementContents';
import withI18n from '../utils/withI18n';
import withErrorHandler from '../utils/withErrorHandler';
import { decimalPlaces, decRound } from '../utils/number';
import FlexBox from './FlexBox';
import Icon from './Icon';
import FiatPrice from './FiatPrice';
import { setPriceAndAmount as setPriceAndAmountAction } from '../actions/actionsSet/order';

// for performance, avoids creating a RegExp each call
const getPriceMatcherRegexp = memoize(decimals => decimals === 0 ? new RegExp('^[0-9]*') : new RegExp(`^[0-9]*\\.[0-9]{${decimals}}`));

const StyledFlexBox = styled(FlexBox)`
  align-items: center;
  background: ${({ theme }) => theme.boxheadbg};
  font-size: 18px;
  /* justify-content: center; */
  padding: 0 10px;
`;

const MoreBox = styled.a`
  display: inline-block;
  align-items: center;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  text-decoration-line: underline;
  color: #666666;
  &:hover,
  &:active {
    color: #f0b90b!important;
  }
  text-decoration: underline !important;
  padding: 0 10px;
`;

const StyledFlexBoxInner = styled(FlexBox)`
  position: absolute;
  align-items: center;
  background: ${({ theme }) => theme.boxheadbg};
  font-size: 18px;
  justify-content: center;
  padding: 0 10px;
`;

const NetworkIndicator = styled.img.attrs(({ depthSocketConnected }) => ({
  src: depthSocketConnected
    ? require('./CurrentPrice/depthSuc.png')
    : require('./CurrentPrice/depthFail.gif'),
}))`
  width: 18px;
  height: 13px;
`;

export const Amount = styled.span`
  display: flex;
  align-items: center;
  font-weight: 700;
  white-space: nowrap;
  & > span {
    cursor: default;
    user-select: auto;
    &:not(:only-child) {
      margin-right: 1px;
    }
  }
`;

export const StyledFiatPrice = styled.div`
  font-size: ${({ theme }) => theme.fontSizeMd};
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  margin-left: 5px;
`;

export const GreenAmount = styled(Amount)`
  color: ${({ theme }) => theme.up};
`;
export const RedAmount = styled(Amount)`
  color: ${({ theme }) => theme.down};
  & > i {
    transform: scaleY(-1);
  }
`;
export const LoadingAmount = Amount;

export type Props = {
  children: number | string | React.Element<*> | Array<any>,
  onNewPriceClick?: Function,
  symbol: string,
};

const ClickablePrice = (props: Props) => {
  const { children, onNewPriceClick, symbol } = props;

  return (
    <span
      onClick={ev => {
        selectElementContents(ev.target);
        onNewPriceClick && onNewPriceClick({ price: children, symbol });
      }}
      onKeyPress={ev => {
        selectElementContents(ev.target);
      }}
      onBlur={ev => {
        selectElementContents(ev.target).removeAllRanges();
      }}
      tabIndex={-1}
      role="button"
    >
      {children}
    </span>
  );
};

ClickablePrice.defaultProps = {
  onNewPriceClick: () => {},
};

export const showPrice = (
  current: string,
  last: string,
  decimals: number,
  onNewPriceClick: Function,
  symbol: string
) => {
  const matcher = getPriceMatcherRegexp(decimals);
  let display = current.match(matcher);
  if (!display) display = [current];
  const fcurrent = parseFloat(current);
  const flast = parseFloat(last);
  if (fcurrent > flast) {
    return (
      <GreenAmount>
        <ClickablePrice onNewPriceClick={onNewPriceClick} symbol={symbol}>
          {display[0]}
        </ClickablePrice>
        <Icon type="zhang" size={12} />
      </GreenAmount>
    );
  }
  if (fcurrent < flast) {
    return (
      <RedAmount>
        <ClickablePrice onNewPriceClick={onNewPriceClick} symbol={symbol}>
          {display[0]}
        </ClickablePrice>
        <Icon type="zhang" size={12} />
      </RedAmount>
    );
  }
  return (
    <Amount>
      <ClickablePrice onNewPriceClick={onNewPriceClick} symbol={symbol}>
        {display[0]}
      </ClickablePrice>
    </Amount>
  );
};

const getTickSize = ticker => {
  try {
    return decimalPlaces(ticker.tickSize.toString());
  } catch (err) {
    return 8;
  }
};

const mapStateToProps = (
  // eslint-disable-next-line object-curly-newline
  { trades, tickers, depthSocketConnected, currencyList, userPreferredCurrency, locale },
  { symbol }
) => ({
  trades: trades[symbol] || [],
  ticker: tickers[symbol] || {},
  tickSize: getTickSize(tickers[symbol]),
  depthSocketConnected,
  currencyList,
  preferredCurrency: userPreferredCurrency,
  locale,
});

// eslint-disable-next-line object-curly-newline
const renderPrice = (trades, ticker, { decimals, setPriceAndAmount, symbol, tickSize }) => {
  if (trades && trades.length > 2) {
    return (
      <strong>{showPrice(trades[0].p, trades[1].p, decimals, setPriceAndAmount, symbol)}</strong>
    );
  }
  return <LoadingAmount>{!ticker.c ? '—' : decRound(ticker.c, tickSize)}</LoadingAmount>;
};

const renderNetworkIndicator = (depthSocketConnected, title) => (
  <NetworkIndicator depthSocketConnected={depthSocketConnected} alt={title} title={title} />
);

const renderFiatPrice = (ticker, currencyList, preferredCurrency) => (
  <StyledFiatPrice>
    <FiatPrice
      currencyList={currencyList}
      price={ticker.c}
      quoteAsset={ticker.qa}
      preferredCurrency={preferredCurrency}
    />
  </StyledFiatPrice>
);

export default connect(
  mapStateToProps,
  { setPriceAndAmount: setPriceAndAmountAction }
)(
  withI18n(
    withErrorHandler(props => {
      const title = props.depthSocketConnected
        ? (props.i18n || {}).depthTip_normal || 'Market Status: Running'
        : (props.i18n || {}).depthTip_error || 'Market Status: Delay';
      const morei18n = (props.i18n || {})['exchange-TradeAbout-More'] || 'More';
      const {
        trades,
        ticker,
        decimals,
        setPriceAndAmount,
        symbol,
        tickSize,
        depthSocketConnected,
        currencyList,
        preferredCurrency,
        locale,
        base,
      } = props;
      return (
        <React.Fragment>
          <StyledFlexBox {...props}>
            {/* <FlexBox flex={1} /> */}
            <StyledFlexBoxInner>
              {renderPrice(trades, ticker, {
                decimals,
                setPriceAndAmount,
                symbol,
                tickSize,
              })}
              {renderFiatPrice(ticker, currencyList, preferredCurrency)}
            </StyledFlexBoxInner>
            <FlexBox flex={1} />
            <MoreBox href={`/${locale}/orderbook/${symbol.replace(base, `${base}_`)}`}>
              {morei18n}
            </MoreBox>
            {renderNetworkIndicator(depthSocketConnected, title)}
          </StyledFlexBox>
        </React.Fragment>
      );
    })
  )
);
